// 📄 SERVIÇO PARA EXPORTAÇÃO DE RELATÓRIO PDF - VERSÃO CORRIGIDA SEM ERRO UNICODE
// src/services/relatorioExportService.js

import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- UTF-8 helpers (sanitizar texto) ---
const EMOJI_REGEX = /[\p{Extended_Pictographic}\uFE0F]/gu;
const sanitizeText = (t) => {
  if (!t) return '';
  return String(t)
    .normalize('NFC')
    .replace(EMOJI_REGEX, '') // Remove emojis
    .replace(/[^\x00-\x7F\u00C0-\u017F\u0100-\u024F]/g, ''); // Mantém apenas caracteres latinos
};

export class RelatorioExportService {
  constructor() {
    // Configuração melhorada do PDF
    this.doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    this.currentY = 20;
    this.pageHeight = 280;
    this.margin = 20;
    this._fontsReady = false;
    
    // Paleta de cores consistente
    this.colors = {
      primary: [0, 123, 255],
      success: [40, 167, 69],
      warning: [255, 193, 7],
      danger: [220, 53, 69],
      info: [23, 162, 184],
      light: [248, 249, 250],
      dark: [52, 58, 64],
      gray: [108, 117, 125]
    };
  }

  /**
   * 🔤 CONFIGURAR FONTES DE FORMA SEGURA
   */
  async _ensureFonts() {
    if (this._fontsReady) return;
    
    try {
      // Usar fontes built-in do jsPDF que suportam acentos
      this.doc.setFont('helvetica', 'normal');
      
      // Adicionar suporte básico a caracteres especiais
      this.doc.setLanguage('pt-BR');
      
      this._fontsReady = true;
      console.log('✅ Fontes configuradas com sucesso (Helvetica com UTF-8)');
    } catch (error) {
      console.warn('⚠️ Erro ao configurar fontes:', error);
      // Fallback absoluto
      this.doc.setFont('helvetica', 'normal');
      this._fontsReady = true;
    }
  }

  /**
   * 📝 WRAPPER PARA IMPRIMIR TEXTO SEMPRE SANITIZADO
   */
  _text(txt, x, y, opts = {}) {
    const textoLimpo = sanitizeText(txt);
    this.doc.text(textoLimpo, x, y, opts);
  }

  /**
   * 🎯 FUNÇÃO PRINCIPAL: Exportar relatório completo
   */
  async exportarRelatorioIntegridade(dadosMonografia, dadosSimilaridade) {
    try {
      console.log('📊 Iniciando exportação do relatório...');
      
      // Configurar fontes de forma segura
      await this._ensureFonts();
      
      this.currentY = 20;

      // 1. Cabeçalho institucional melhorado
      this._adicionarCabecalhoMelhorado();

      // 2. Informações da monografia
      this._adicionarInfoMonografiaMelhorada(dadosMonografia);

      // 3. Resumo executivo com cards melhorados
      this._adicionarResumoExecutivoMelhorado(dadosSimilaridade);

      // 4. Detalhes de verificação de IA
      if (dadosSimilaridade.iaDetection?.disponivel) {
        this._adicionarDeteccaoIAMelhorada(dadosSimilaridade.iaDetection);
      }

      // 5. Sistema anti-plágio
      this._adicionarSistemaAntiPlagioMelhorado(dadosSimilaridade.antiPlagio);

      // 6. Documentos similares encontrados
      if (dadosSimilaridade.similares?.length > 0) {
        this._adicionarDocumentosSimilaresMelhorados(dadosSimilaridade.similares);
      }

      // 7. Análise detalhada de similaridade
      this._adicionarAnaliseDetalhadaMelhorada(dadosSimilaridade.similares);

      // 8. Conclusões e recomendações
      this._adicionarConclusoesMelhoradas(dadosSimilaridade);

      // 9. Rodapé com numeração
      this._adicionarRodapeMelhorado();

      // Salvar arquivo
      const nomeArquivo = `Relatorio_Integridade_${dadosMonografia.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      this.doc.save(nomeArquivo);

      console.log('✅ Relatório exportado com sucesso!');
      return { success: true, filename: nomeArquivo };

    } catch (error) {
      console.error('Erro ao exportar:', error);
      
      // Notificação de erro melhorada
      if (typeof window !== 'undefined') {
        const Toast = window.Swal || window.alert;
        if (window.Swal) {
          Toast.fire({
            icon: 'error',
            title: 'Erro na Exportação',
            text: `Falha ao gerar relatório: ${error.message}`,
            showConfirmButton: true
          });
        } else {
          alert(`Erro ao exportar relatório: ${error.message}`);
        }
      }
      
      throw error;
    }
  }

  /**
   * 📋 CABEÇALHO INSTITUCIONAL MELHORADO
   */
  _adicionarCabecalhoMelhorado() {
    // Fundo azul para o cabeçalho
    this.doc.setFillColor(...this.colors.primary);
    this.doc.roundedRect(this.margin, this.currentY, 170, 20, 3, 3, 'F');
    
    // Título principal em branco
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this._text('LOGOTIPO E NOME DA INSTITUIÇÃO', 105, this.currentY + 8, { align: 'center' });
    
    this.doc.setFontSize(10);
    this._text('RELATÓRIO DE INTEGRIDADE ACADÊMICA', 105, this.currentY + 15, { align: 'center' });
    
    this.currentY += 30;
    
    // Data e hora de geração
    const agora = new Date();
    const dataHora = `Gerado em ${agora.toLocaleDateString('pt-BR')} as ${agora.toLocaleTimeString('pt-BR')}`;
    
    this.doc.setFontSize(8);
    this.doc.setTextColor(...this.colors.gray);
    this._text(dataHora, 190, this.currentY, { align: 'right' });
    
    this.currentY += 15;
  }

  /**
   * 🎨 MÉTODO PARA ADICIONAR TÍTULOS DE SEÇÃO
   */
  _adicionarTituloSecao(titulo, cor = this.colors.primary) {
    this._verificarNovaPagina(25);
    
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...this.colors.dark);
    
    // Título sanitizado
    this._text(titulo, this.margin, this.currentY);
    
    // Linha decorativa colorida
    this.doc.setDrawColor(...cor);
    this.doc.setLineWidth(1);
    this.doc.line(this.margin, this.currentY + 3, this.margin + 60, this.currentY + 3);
    
    this.currentY += 12;
  }

  /**
   * 📝 INFORMAÇÕES DA MONOGRAFIA MELHORADAS
   */
  _adicionarInfoMonografiaMelhorada(dados) {
    this._adicionarTituloSecao('Detalhes da Monografia');

    // Card de informações
    this.doc.setFillColor(...this.colors.light);
    this.doc.roundedRect(this.margin, this.currentY, 170, 45, 5, 5, 'F');
    this.doc.setDrawColor(200, 200, 200);
    this.doc.roundedRect(this.margin, this.currentY, 170, 45, 5, 5);

    const info = [
      ['ID do Projeto:', sanitizeText(String(dados.id || 'N/A'))],
      ['Titulo:', sanitizeText(String(dados.titulo || 'N/A'))],
      ['Estudante:', sanitizeText(String(dados.estudante || 'N/A'))],
      ['Supervisor:', sanitizeText(String(dados.supervisor || 'N/A'))],
      ['Data de Submissao:', dados.dataSubmissao ? new Date(dados.dataSubmissao).toLocaleDateString('pt-BR') : 'N/A'],
      ['Versao:', sanitizeText(String(dados.versao || '1.0'))],
      ['Status:', sanitizeText(String(dados.status || 'N/A'))]
    ];

    autoTable(this.doc, {
      startY: this.currentY + 5,
      head: [],
      body: info,
      theme: 'plain',
      styles: { 
        fontSize: 10, 
        cellPadding: 3,
        textColor: this.colors.dark
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 115 }
      },
      margin: { left: this.margin + 5, right: this.margin + 5 }
    });

    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  /**
   * 📊 RESUMO EXECUTIVO MELHORADO
   */
  _adicionarResumoExecutivoMelhorado(dados) {
    this._adicionarTituloSecao('Resumo Executivo');

    const cards = [
      {
        titulo: 'Similaridade Maxima',
        valor: dados.similares?.length > 0 ? `${Math.max(...dados.similares.map(s => s.score || 0))}%` : '0%',
        status: dados.similares?.length > 0 ? 'Normal' : 'Sem Similaridade',
        cor: dados.similares?.length > 0 && Math.max(...dados.similares.map(s => s.score || 0)) > 70 
          ? this.colors.danger : this.colors.success
      },
      {
        titulo: 'Deteccao de IA',
        valor: dados.iaDetection?.disponivel ? `${dados.iaDetection.score || 0}%` : 'N/A',
        status: sanitizeText(dados.iaDetection?.classificacao || 'Nao Disponivel'),
        cor: dados.iaDetection?.score > 70 ? this.colors.danger : 
             dados.iaDetection?.score > 30 ? this.colors.warning : this.colors.success
      },
      {
        titulo: 'Fontes Verificadas',
        valor: String(dados.antiPlagio?.totais?.total || '0'),
        status: dados.antiPlagio?.sistemaHibrido ? 'Sistema Hibrido' : 'Sistema Basico',
        cor: this.colors.info
      }
    ];

    cards.forEach((card, index) => {
      const x = this.margin + (index * 57);
      this._adicionarCardMelhorado(x, this.currentY, card.titulo, card.valor, card.status, card.cor);
    });

    this.currentY += 45;
  }

  /**
   * 🎨 CARD MELHORADO
   */
  _adicionarCardMelhorado(x, y, titulo, valor, status, cor) {
    // Sombra do card
    this.doc.setFillColor(220, 220, 220);
    this.doc.roundedRect(x + 1, y + 1, 55, 35, 5, 5, 'F');
    
    // Fundo do card
    this.doc.setFillColor(255, 255, 255);
    this.doc.roundedRect(x, y, 55, 35, 5, 5, 'F');
    
    // Borda colorida superior
    this.doc.setFillColor(...cor);
    this.doc.roundedRect(x, y, 55, 5, 5, 5, 'F');
    
    // Contorno do card
    this.doc.setDrawColor(230, 230, 230);
    this.doc.roundedRect(x, y, 55, 35, 5, 5);
    
    // Título
    this.doc.setFontSize(8);
    this.doc.setTextColor(...this.colors.gray);
    this.doc.setFont('helvetica', 'normal');
    const tituloLines = this.doc.splitTextToSize(sanitizeText(titulo), 50);
    this._text(tituloLines.join(' '), x + 3, y + 12);
    
    // Valor principal
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...cor);
    this._text(String(valor), x + 3, y + 22);
    
    // Status
    this.doc.setFontSize(7);
    this.doc.setTextColor(...this.colors.gray);
    this.doc.setFont('helvetica', 'normal');
    const statusLines = this.doc.splitTextToSize(sanitizeText(status), 50);
    this._text(statusLines.join(' '), x + 3, y + 30);
  }

  /**
   * 🤖 DETECÇÃO DE IA MELHORADA
   */
  _adicionarDeteccaoIAMelhorada(iaDetection) {
    this._adicionarTituloSecao('Verificacao de Conteudo Gerado por IA', this.colors.info);

    const score = iaDetection.score || 0;
    
    // Barra de progresso melhorada
    const barWidth = 150;
    const barHeight = 12;
    
    // Fundo da barra com bordas arredondadas
    this.doc.setFillColor(240, 240, 240);
    this.doc.roundedRect(this.margin, this.currentY, barWidth, barHeight, 6, 6, 'F');
    
    // Barra de progresso colorida
    const fillWidth = (score / 100) * barWidth;
    const cor = score < 30 ? this.colors.success : score < 70 ? this.colors.warning : this.colors.danger;
    this.doc.setFillColor(...cor);
    this.doc.roundedRect(this.margin, this.currentY, fillWidth, barHeight, 6, 6, 'F');
    
    // Texto do score na barra
    this.doc.setFontSize(9);
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFont('helvetica', 'bold');
    this._text(`${score}%`, this.margin + fillWidth/2, this.currentY + 8, { align: 'center' });

    this.currentY += 20;

    // Detalhes em tabela melhorada
    const detalhes = [
      ['Score de IA:', `${score}%`],
      ['Classificacao:', sanitizeText(String(iaDetection.classificacao || 'N/A'))],
      ['Recomendacao:', sanitizeText(String(iaDetection.recomendacao || 'N/A'))],
      ['Confianca:', iaDetection.detalhes?.confianca ? `${iaDetection.detalhes.confianca}%` : 'N/A']
    ];

    autoTable(this.doc, {
      startY: this.currentY,
      head: [],
      body: detalhes,
      theme: 'striped',
      styles: { 
        fontSize: 10, 
        cellPadding: 4,
        alternateRowStyles: { fillColor: [248, 249, 250] }
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 120 }
      },
      margin: { left: this.margin, right: this.margin }
    });

    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  /**
   * 🛡️ SISTEMA ANTI-PLÁGIO MELHORADO
   */
  _adicionarSistemaAntiPlagioMelhorado(antiPlagio) {
    this._adicionarTituloSecao('Sistema Anti-Plagio', this.colors.success);

    const config = [
      ['Configuracao:', antiPlagio.sistemaHibrido ? 'Sistema Hibrido' : 'Sistema Basico'],
      ['Limite de Deteccao:', `${(antiPlagio.threshold * 100) || 30}%`],
      ['Monografias Verificadas:', String(antiPlagio.totais?.monografias || '0')],
      ['Arquivos Digitais:', String(antiPlagio.totais?.arquivos_digitais || '0')],
      ['Fontes Externas:', String(antiPlagio.totais?.fontes_externas || '0')]
    ];

    autoTable(this.doc, {
      startY: this.currentY,
      head: [['Parametro', 'Valor']],
      body: config,
      theme: 'striped',
      headStyles: { 
        fontStyle: 'bold',
        fillColor: this.colors.success,
        textColor: [255, 255, 255],
        fontSize: 11
      },
      styles: { 
        fontSize: 10, 
        cellPadding: 4,
        alternateRowStyles: { fillColor: [248, 249, 250] }
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 110 }
      },
      margin: { left: this.margin, right: this.margin }
    });

    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  /**
   * 📋 DOCUMENTOS SIMILARES MELHORADOS
   */
  _adicionarDocumentosSimilaresMelhorados(similares) {
    this._adicionarTituloSecao('Documentos Similares Encontrados', this.colors.warning);

    if (similares.length === 0) {
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...this.colors.success);
      this._text('Nenhum documento similar foi encontrado.', this.margin, this.currentY);
      this.currentY += 15;
      return;
    }

    const tableData = similares.map((item, index) => [
      String(index + 1),
      item.tipo === 'monografia' ? 'Monografia' : 'Arquivo',
      this._truncateText(sanitizeText(String(item.documentoOriginal?.titulo || 'Sem titulo')), 35),
      sanitizeText(String(item.documentoOriginal?.estudante || 'Nao identificado')),
      item.documentoOriginal?.dataSubmissao ? 
        new Date(item.documentoOriginal.dataSubmissao).toLocaleDateString('pt-BR') : '—',
      `${item.score || 0}%`
    ]);

    autoTable(this.doc, {
      startY: this.currentY,
      head: [['#', 'Tipo', 'Titulo', 'Autor', 'Data', 'Similaridade']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fontStyle: 'bold',
        fillColor: this.colors.warning,
        textColor: [255, 255, 255],
        fontSize: 10
      },
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        alternateRowStyles: { fillColor: [254, 252, 242] }
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 25 },
        2: { cellWidth: 45 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 20, halign: 'center', fontStyle: 'bold' }
      },
      margin: { left: this.margin, right: this.margin }
    });

    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  /**
   * 🔍 ANÁLISE DETALHADA MELHORADA
   */
  _adicionarAnaliseDetalhadaMelhorada(similares) {
    if (!similares || similares.length === 0) return;

    this._adicionarTituloSecao('Analise Detalhada de Similaridade', this.colors.info);

    similares.slice(0, 3).forEach((item, index) => {
      this._verificarNovaPagina(40);

      // Card do documento
      this.doc.setFillColor(...this.colors.light);
      this.doc.roundedRect(this.margin, this.currentY, 170, 30, 5, 5, 'F');
      this.doc.setDrawColor(200, 200, 200);
      this.doc.roundedRect(this.margin, this.currentY, 170, 30, 5, 5);

      // Título do documento
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(...this.colors.dark);
      const titulo = this._truncateText(sanitizeText(`Documento ${index + 1}: ${item.documentoOriginal?.titulo || 'Sem titulo'}`), 60);
      this._text(titulo, this.margin + 5, this.currentY + 8);

      // Métricas lado a lado
      const metricas = [
        `Similaridade: ${((item.similaridadeDireta || 0) * 100).toFixed(1)}%`,
        `Jaro-Winkler: ${((item.jaroWinkler || 0) * 100).toFixed(1)}%`,
        `Score Final: ${item.score || 0}%`
      ];

      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...this.colors.gray);
      metricas.forEach((metrica, i) => {
        this._text(metrica, this.margin + 5 + (i * 55), this.currentY + 16);
      });

      // Termos em comum
      if (item.termosComuns?.length > 0) {
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'bold');
        this._text('Termos similares:', this.margin + 5, this.currentY + 24);
        
        this.doc.setFont('helvetica', 'normal');
        const termosTexto = item.termosComuns.slice(0, 8).join(', ');
        const termosTruncados = this._truncateText(sanitizeText(termosTexto), 75);
        this._text(termosTruncados, this.margin + 35, this.currentY + 24);
      }

      this.currentY += 40;
    });
  }

  /**
   * 📝 CONCLUSÕES MELHORADAS
   */
  _adicionarConclusoesMelhoradas(dados) {
    this._adicionarTituloSecao('Conclusoes e Recomendacoes', this.colors.primary);

    const maxSimilaridade = dados.similares?.length > 0 ? 
      Math.max(...dados.similares.map(s => s.score || 0)) : 0;
    
    const iaScore = dados.iaDetection?.score || 0;
    
    let conclusao = '';
    let recomendacao = '';
    let cor = this.colors.success;
    let icone = 'APROVADO';

    if (maxSimilaridade > 70 || iaScore > 70) {
      conclusao = 'ATENCAO REQUERIDA: Foram detectados niveis elevados de similaridade ou possivel uso de IA.';
      recomendacao = 'Recomenda-se revisao manual detalhada e possivel solicitacao de esclarecimentos ao estudante.';
      cor = this.colors.danger;
      icone = 'ATENCAO';
    } else if (maxSimilaridade > 30 || iaScore > 30) {
      conclusao = 'REVISAO RECOMENDADA: Foram detectados niveis moderados que merecem atencao.';
      recomendacao = 'Sugere-se verificacao dos trechos similares e analise contextual.';
      cor = this.colors.warning;
      icone = 'REVISAO';
    } else {
      conclusao = 'APROVADO: O documento apresenta niveis aceitaveis de originalidade.';
      recomendacao = 'O trabalho pode prosseguir no fluxo normal de avaliacao.';
    }

    // Card de conclusão
    this.doc.setFillColor(255, 255, 255);
    this.doc.roundedRect(this.margin, this.currentY, 170, 35, 8, 8, 'F');
    this.doc.setDrawColor(...cor);
    this.doc.setLineWidth(2);
    this.doc.roundedRect(this.margin, this.currentY, 170, 35, 8, 8);

    // Título
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...cor);
    this._text(`${icone} - CONCLUSAO`, this.margin + 8, this.currentY + 12);
    
    // Texto da conclusão
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...this.colors.dark);
    const conclusaoLines = this.doc.splitTextToSize(sanitizeText(conclusao), 150);
    conclusaoLines.forEach((line, i) => {
      this._text(line, this.margin + 8, this.currentY + 20 + (i * 5));
    });

    this.currentY += 45;

    // Recomendação
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...this.colors.dark);
    this._text('RECOMENDACAO:', this.margin, this.currentY);
    
    this.currentY += 8;
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    const recomendacaoLines = this.doc.splitTextToSize(sanitizeText(recomendacao), 170);
    recomendacaoLines.forEach((line, i) => {
      this._text(line, this.margin, this.currentY + (i * 5));
    });
    
    this.currentY += recomendacaoLines.length * 5 + 15;
  }

  /**
   * 📄 RODAPÉ MELHORADO
   */
  _adicionarRodapeMelhorado() {
    const totalPages = this.doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      
      // Linha do rodapé
      this.doc.setDrawColor(...this.colors.gray);
      this.doc.setLineWidth(0.3);
      this.doc.line(this.margin, this.pageHeight + 8, 190, this.pageHeight + 8);
      
      // Texto do rodapé
      this.doc.setFontSize(8);
      this.doc.setTextColor(...this.colors.gray);
      this.doc.setFont('helvetica', 'normal');
      
      const agora = new Date();
      const dataGeracao = `Gerado em ${agora.toLocaleDateString('pt-BR')} as ${agora.toLocaleTimeString('pt-BR')}`;
      
      this._text(dataGeracao, this.margin, this.pageHeight + 15);
      this._text('Sistema de Gestao de Monografias', 105, this.pageHeight + 15, { align: 'center' });
      this._text(`Pagina ${i} de ${totalPages}`, 190, this.pageHeight + 15, { align: 'right' });
    }
  }

  /**
   * 🔧 MÉTODOS UTILITÁRIOS
   */
  _verificarNovaPagina(alturaMinima) {
    if (this.currentY + alturaMinima > this.pageHeight) {
      this.doc.addPage();
      this.currentY = 20;
    }
  }

  _truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  _adicionarTextoFormatado(texto, largura, tamanhoFonte = 10) {
this.doc.setFontSize(tamanhoFonte);
    const lines = this.doc.splitTextToSize(sanitizeText(texto), largura);
    lines.forEach((line, i) => {
      this._text(line, this.margin, this.currentY + (i * (tamanhoFonte / 2.5)));
    });
    this.currentY += lines.length * (tamanhoFonte / 2.5);
    return lines.length * (tamanhoFonte / 2.5);
  }
}

// 🎯 FUNÇÃO UTILITÁRIA PARA USO NO COMPONENTE
export const exportarRelatorioIntegridade = async (dadosMonografia, dadosSimilaridade) => {
  const exporter = new RelatorioExportService();
  return await exporter.exportarRelatorioIntegridade(dadosMonografia, dadosSimilaridade);
};

// 📦 HOOK REACT MELHORADO
export const useRelatorioExport = () => {
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState(null);
  const [progresso, setProgresso] = useState(0);

  const exportar = async (dadosMonografia, dadosSimilaridade) => {
    try {
      setExportando(true);
      setErro(null);
      setProgresso(0);
      
      // Simular progresso
      const intervalos = [20, 40, 60, 80, 90];
      for (const p of intervalos) {
        setProgresso(p);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const resultado = await exportarRelatorioIntegridade(dadosMonografia, dadosSimilaridade);
      
      setProgresso(100);
      
      // Notificação de sucesso melhorada
      if (typeof window !== 'undefined') {
        const Toast = window.Swal || window.alert;
        if (window.Swal) {
          Toast.fire({
            icon: 'success',
            title: 'Relatório Exportado!',
            text: `Arquivo: ${resultado.filename}`,
            timer: 3000,
            showConfirmButton: false
          });
        } else {
          alert(`Relatório exportado com sucesso!\nArquivo: ${resultado.filename}`);
        }
      }
      
      return resultado;
    } catch (error) {
      console.error('Erro ao exportar:', error);
      setErro(error.message);
      setProgresso(0);
      
      // Notificação de erro melhorada
      if (typeof window !== 'undefined') {
        const Toast = window.Swal || window.alert;
        if (window.Swal) {
          Toast.fire({
            icon: 'error',
            title: 'Erro na Exportação',
            text: `Falha ao gerar relatório: ${error.message}`,
            showConfirmButton: true
          });
        } else {
          alert(`Erro ao exportar relatório: ${error.message}`);
        }
      }
      
      throw error;
    } finally {
      setExportando(false);
    }
  };

  return { 
    exportar, 
    exportando, 
    erro, 
    progresso,
    // Função adicional para limpar erros
    limparErro: () => setErro(null)
  };
};

// 🎨 EXEMPLO DE USO NO COMPONENTE REACT
export const ExemploUsoRelatorio = () => {
  const { exportar, exportando, erro, progresso, limparErro } = useRelatorioExport();

  const dadosExemplo = {
    monografia: {
      id: 'MON-2024-001',
      titulo: 'Análise de Sistemas de Inteligência Artificial',
      estudante: 'João Silva Santos',
      supervisor: 'Prof. Dr. Maria Oliveira',
      dataSubmissao: new Date(),
      versao: '2.1',
      status: 'Em Revisão'
    },
    similaridade: {
      similares: [
        {
          tipo: 'monografia',
          score: 25,
          documentoOriginal: {
            titulo: 'Estudo sobre Machine Learning',
            estudante: 'Ana Costa',
            dataSubmissao: new Date('2023-10-15')
          },
          similaridadeDireta: 0.23,
          jaroWinkler: 0.27,
          termosComuns: ['inteligência', 'artificial', 'machine learning', 'dados']
        }
      ],
      iaDetection: {
        disponivel: true,
        score: 15,
        classificacao: 'Baixo Risco',
        recomendacao: 'Documento aprovado para prosseguir',
        detalhes: { confianca: 85 }
      },
      antiPlagio: {
        sistemaHibrido: true,
        threshold: 0.3,
        totais: {
          monografias: 1250,
          arquivos_digitais: 3400,
          fontes_externas: 5600,
          total: 10250
        }
      }
    }
  };

  const handleExportar = async () => {
    try {
      await exportar(dadosExemplo.monografia, dadosExemplo.similaridade);
    } catch (error) {
      console.error('Falha na exportação:', error);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5>Exportação de Relatório PDF</h5>
      </div>
      <div className="card-body">
        <button 
          onClick={handleExportar} 
          disabled={exportando}
          className={`btn ${exportando ? 'btn-secondary' : 'btn-primary'} mb-3`}
        >
          {exportando ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Exportando... {progresso}%
            </>
          ) : (
            <>Exportar Relatório de Integridade</>
          )}
        </button>
        
        {exportando && (
          <div className="progress mb-3" style={{ height: '20px' }}>
            <div 
              className="progress-bar progress-bar-striped progress-bar-animated" 
              role="progressbar"
              style={{ width: `${progresso}%` }}
            >
              {progresso}%
            </div>
          </div>
        )}
        
        {erro && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Erro na Exportação:</strong> {erro}
            <button 
              type="button" 
              className="btn-close" 
              onClick={limparErro}
              aria-label="Fechar"
            ></button>
          </div>
        )}
        
        <div className="mt-3">
          <h6>Recursos do Relatório:</h6>
          <ul className="list-group list-group-flush">
            <li className="list-group-item">Layout profissional com design moderno</li>
            <li className="list-group-item">Cards visuais com indicadores coloridos</li>
            <li className="list-group-item">Barras de progresso para scores de IA</li>
            <li className="list-group-item">Tabelas organizadas com estilo alternado</li>
            <li className="list-group-item">Análise detalhada de similaridade</li>
            <li className="list-group-item">Conclusões e recomendações automatizadas</li>
            <li className="list-group-item">Rodapé profissional com numeração</li>
            <li className="list-group-item">Suporte a caracteres especiais sem erro Unicode</li>
            <li className="list-group-item">Sanitização automática de texto problemático</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// 🔧 UTILITÁRIOS ADICIONAIS
export const validarDadosRelatorio = (dadosMonografia, dadosSimilaridade) => {
  const erros = [];
  
  // Validar dados da monografia
  if (!dadosMonografia?.id) erros.push('ID da monografia é obrigatório');
  if (!dadosMonografia?.titulo) erros.push('Título da monografia é obrigatório');
  if (!dadosMonografia?.estudante) erros.push('Nome do estudante é obrigatório');
  
  // Validar dados de similaridade
  if (!dadosSimilaridade) erros.push('Dados de similaridade são obrigatórios');
  if (!dadosSimilaridade?.antiPlagio) erros.push('Configuração anti-plágio é obrigatória');
  
  return {
    valido: erros.length === 0,
    erros
  };
};

export const gerarDadosExemplo = () => {
  return {
    monografia: {
      id: `MON-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
      titulo: 'Exemplo de Monografia para Teste',
      estudante: 'Estudante Exemplo',
      supervisor: 'Prof. Supervisor Exemplo',
      dataSubmissao: new Date(),
      versao: '1.0',
      status: 'Em Análise'
    },
    similaridade: {
      similares: [],
      iaDetection: {
        disponivel: true,
        score: Math.floor(Math.random() * 100),
        classificacao: 'Teste',
        recomendacao: 'Documento de exemplo',
        detalhes: { confianca: 90 }
      },
      antiPlagio: {
        sistemaHibrido: true,
        threshold: 0.3,
        totais: {
          monografias: 1000,
          arquivos_digitais: 2000,
          fontes_externas: 3000,
          total: 6000
        }
      }
    }
  };
};

// 🚀 EXEMPLO DE USO SIMPLES NO COMPONENTE
/*
// Em um componente React:
import React from 'react';
import { useRelatorioExport } from './services/relatorioExportService';

function MeuComponente() {
  const { exportar, exportando, erro, progresso, limparErro } = useRelatorioExport();

  const handleExportar = async () => {
    try {
      await exportar(dadosMonografia, dadosSimilaridade);
    } catch (error) {
      console.error('Falha na exportação:', error);
    }
  };

  return (
    <div>
      <button 
        onClick={handleExportar} 
        disabled={exportando}
        className="btn btn-primary"
      >
        {exportando ? `Exportando... ${progresso}%` : 'Exportar Relatório'}
      </button>
      
      {exportando && (
        <div className="progress mt-2">
          <div 
            className="progress-bar" 
            style={{ width: `${progresso}%` }}
          >
            {progresso}%
          </div>
        </div>
      )}
      
      {erro && (
        <div className="alert alert-danger mt-2">
          <strong>Erro:</strong> {erro}
          <button 
            className="btn-close" 
            onClick={limparErro}
            aria-label="Fechar"
          ></button>
        </div>
      )}
    </div>
  );
}
*/

// 📋 INSTRUÇÕES DE INSTALAÇÃO E USO CORRIGIDO
/*
INSTALAÇÃO DAS DEPENDÊNCIAS:
npm install jspdf jspdf-autotable

CORREÇÕES IMPLEMENTADAS:
✅ Removido carregamento de fontes TTF customizadas (fonte do erro)
✅ Usando apenas fontes built-in do jsPDF (Helvetica)
✅ Sanitização melhorada de texto para remover caracteres problemáticos
✅ Substituição de acentos por caracteres simples nos títulos
✅ Fallback robusto para evitar erros de encoding

IMPORTAÇÃO NO SEU PROJETO:
import { 
  useRelatorioExport, 
  exportarRelatorioIntegridade,
  validarDadosRelatorio,
  gerarDadosExemplo 
} from './services/relatorioExportService';

ESTRUTURA DOS DADOS ESPERADOS:
- dadosMonografia: { id, titulo, estudante, supervisor, dataSubmissao, versao, status }
- dadosSimilaridade: { similares[], iaDetection, antiPlagio }

RECURSOS PRINCIPAIS:
✅ Geração de PDF profissional sem erros de Unicode
📊 Cards visuais coloridos para métricas principais
📋 Barras de progresso para scores de IA
🔍 Tabelas organizadas com dados de similaridade
📝 Análise detalhada de documentos similares
🛡️ Conclusões automáticas baseadas nos scores
📄 Sistema de paginação com rodapé profissional
🔧 Tratamento robusto de erros e validação de dados
🌍 Sanitização automática de texto problemático

SOLUÇÕES IMPLEMENTADAS PARA O ERRO:
1. Removeu carregamento de fontes TTF externas
2. Usa apenas Helvetica (fonte nativa do jsPDF)
3. Sanitiza todos os textos antes de imprimir
4. Remove emojis e caracteres especiais problemáticos
5. Substitui acentos por caracteres simples nos títulos
6. Mantém a funcionalidade completa sem erros

AGORA O RELATÓRIO DEVE FUNCIONAR SEM ERROS DE UNICODE!
*/